import {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getPortfolios, Portfolio } from "../api/client";
import { liveRefetchInterval } from "@/lib/marketHours";

interface IPortfolioContext {
  /** Only real (non-combined) portfolios. Safe to iterate for sums, cards, dropdowns. */
  portfolios: Portfolio[];
  /** The single Rollup "All Portfolios" portfolio. Null until provisioned by the backend. */
  combinedPortfolio: Portfolio | null;
  selectedPortfolioId: number | null;
  selectedPortfolio: Portfolio | null;
  setSelectedPortfolioId: (id: number | null) => void;
  isLoading: boolean;
  refetchPortfolios: () => void;
  /**
   * The portfolio to go back to when leaving the demo: the last one selected that is not
   * the demo, else the combined portfolio, else the first real one. Null when none exists.
   */
  lastRealPortfolioId: number | null;
}

const PortfolioContext = createContext<IPortfolioContext | undefined>(
  undefined,
);

export const PortfolioProvider = ({ children }: { children: ReactNode }) => {
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | null>(
    () => {
      const storedId = localStorage.getItem("selectedPortfolioId");
      return storedId ? JSON.parse(storedId) : null;
    },
  );
  const queryClient = useQueryClient();

  const { data: rawPortfolios = [], isLoading } = useQuery<Portfolio[]>({
    queryKey: ["portfolios"],
    queryFn: getPortfolios,
    staleTime: 1000 * 60 * 5,
    // Poll for fresh intraday live values during market hours (no-op off-session).
    refetchInterval: () => liveRefetchInterval(),
  });

  // --- Context Splitting: keep real and combined portfolios separate ---
  const portfolios = useMemo(
    () => rawPortfolios.filter((p) => !p.is_combined),
    [rawPortfolios],
  );
  const combinedPortfolio = useMemo(() => {
    if (portfolios.length <= 1) return null;
    return rawPortfolios.find((p) => p.is_combined) ?? null;
  }, [rawPortfolios, portfolios]);

  // Effect to persist the selected ID to localStorage
  useEffect(() => {
    if (selectedPortfolioId !== null) {
      localStorage.setItem(
        "selectedPortfolioId",
        JSON.stringify(selectedPortfolioId),
      );
    } else {
      localStorage.removeItem("selectedPortfolioId");
    }
  }, [selectedPortfolioId]);

  // Effect to handle initial selection and validation after portfolios are loaded.
  // The combined portfolio is a valid selection, so check both arrays.
  useEffect(() => {
    if (isLoading) return;

    const allPortfolios = [
      ...portfolios,
      ...(combinedPortfolio ? [combinedPortfolio] : []),
    ];

    if (allPortfolios.length > 0) {
      const currentSelectionExists = allPortfolios.some(
        (p) => p.id === selectedPortfolioId,
      );
      if (!currentSelectionExists) {
        // Default to the combined portfolio if available, otherwise the first real portfolio
        setSelectedPortfolioId(
          combinedPortfolio?.id ?? portfolios[0]?.id ?? null,
        );
      }
    } else {
      setSelectedPortfolioId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolios, combinedPortfolio, isLoading]);

  // Resolve selectedPortfolio from both real and combined portfolios
  const selectedPortfolio = useMemo(() => {
    if (combinedPortfolio && selectedPortfolioId === combinedPortfolio.id) {
      return combinedPortfolio;
    }
    return portfolios.find((p) => p.id === selectedPortfolioId) ?? null;
  }, [portfolios, combinedPortfolio, selectedPortfolioId]);

  // Remembered in memory only. After a reload the fallback below is good enough.
  const [lastSelectedRealId, setLastSelectedRealId] = useState<number | null>(
    null,
  );
  useEffect(() => {
    if (selectedPortfolio && !selectedPortfolio.is_demo) {
      setLastSelectedRealId(selectedPortfolio.id);
    }
  }, [selectedPortfolio]);

  const lastRealPortfolioId = useMemo(() => {
    const stillExists =
      lastSelectedRealId !== null &&
      (combinedPortfolio?.id === lastSelectedRealId ||
        portfolios.some((p) => p.id === lastSelectedRealId));
    if (stillExists) return lastSelectedRealId;
    return (
      combinedPortfolio?.id ?? portfolios.find((p) => !p.is_demo)?.id ?? null
    );
  }, [lastSelectedRealId, combinedPortfolio, portfolios]);

  const refetchPortfolios = useCallback(() => {
    // Fire-and-forget by design (return type is void) — swallow rejections
    // (e.g. an aborted in-flight request on session expiry) so they don't
    // surface as unhandled promise rejections.
    queryClient.refetchQueries({ queryKey: ["portfolios"] }).catch(() => {});
  }, [queryClient]);

  const value = {
    portfolios,
    combinedPortfolio,
    selectedPortfolioId,
    selectedPortfolio,
    setSelectedPortfolioId,
    isLoading,
    refetchPortfolios,
    lastRealPortfolioId,
  };

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
};

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (context === undefined) {
    throw new Error("usePortfolio must be used within a PortfolioProvider");
  }
  return context;
};
